import React, {
  forwardRef, useState, useImperativeHandle, useEffect, useCallback, useRef,
} from 'react';

import cn from 'classnames/bind';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import s from './Slider.module.scss';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css';

const cx = cn.bind(s);

const Slider = forwardRef(
  ({
    children, slideClassName, pagination = true, enabled = true, className, ...options
  }, ref) => {
    const slides = React.Children.toArray(children);
    const slideAfterInit = useRef(0);
    const [slider, setSwiper] = useState(null);

    const handleSwiperInit = useCallback(
      (swiperInstance) => {
        if (enabled) {
          swiperInstance.enable();
          swiperInstance.slideTo(slideAfterInit.current);
        } else {
          swiperInstance.slideTo(0);
          swiperInstance.disable();
        }
        setSwiper(swiperInstance);
      },
      [enabled],
    );

    useImperativeHandle(ref, () => ({
      setSlide(index) {
        if (slider) {
          slider.slideTo(index);
        } else {
          slideAfterInit.current = index;
        }
      },
    }));

    useEffect(() => {
      const onLoad = () => {
        if (slider) {
          slider.update();
        }
      };
      if (document.readyState === 'complete') {
        onLoad();
      } else {
        window.addEventListener('load', onLoad);
      }
      return () => {
        window.removeEventListener('load', onLoad);
      };
    }, [slider]);

    return (
      <Swiper
        modules={[Navigation]}
        className={cx('slider', className, {
          noGutter: !pagination,
        })}
        pagination={pagination ? { clickable: true } : false}
        {...options}
        enabled={enabled}
        onSwiper={handleSwiperInit}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index} className={slideClassName}>
            {slide}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  },
);

Slider.displayName = 'Slider';

export default Slider;
